// Utility functions for chart formatting and configuration

// ─── Unit families ─────────────────────────────────────────────────────────
// A "family" auto-scales within itself: 4_500_000_000 bytes → 4.19 GB.
// Each unit either picks a fixed slot in a family (forces "bytes") or a
// family root that auto-scales (e.g. "bytes-auto" / "seconds-auto"). The
// goal is that an operator can choose "bytes" once and never multiply by
// 1024 in their SQL again.
const UNIT_FAMILIES = {
  bytes: {
    base: 1024,
    units: ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'],
    label: 'IEC bytes',
  },
  bytes_si: {
    base: 1000,
    units: ['B', 'KB', 'MB', 'GB', 'TB', 'PB'],
    label: 'SI bytes',
  },
  bps: {
    base: 1000,
    units: ['bps', 'Kbps', 'Mbps', 'Gbps', 'Tbps'],
    label: 'bits/s',
  },
  Bps: {
    base: 1024,
    units: ['B/s', 'KiB/s', 'MiB/s', 'GiB/s', 'TiB/s'],
    label: 'bytes/s',
  },
  count: {
    base: 1000,
    units: ['', 'K', 'M', 'B', 'T'],
    label: 'short count',
  },
};

// Map a stored unit value to a family + fixed-index (or null = auto).
// Convention: a unit like "GB" means "the value is ALREADY in GB — just
// suffix it"; an "*-auto" unit means "the value is in the family's base
// unit (bytes / bits / etc.) — scale to the nicest unit". This matches
// what operators expect from Datadog/Grafana and avoids double-scaling
// when the SQL already converted (e.g. value / 1024 / 1024 / 1024).
const UNIT_LOOKUP = {
  '': null,
  '%': { kind: 'suffix', text: '%' },
  count: { kind: 'family', family: 'count', index: -1 }, // auto-scale K/M/B
  // Suffix-only — value passed through unchanged.
  bytes: { kind: 'suffix', text: ' B' },
  KB: { kind: 'suffix', text: ' KiB' },
  MB: { kind: 'suffix', text: ' MiB' },
  GB: { kind: 'suffix', text: ' GiB' },
  TB: { kind: 'suffix', text: ' TiB' },
  PB: { kind: 'suffix', text: ' PiB' },
  // Auto-scale.
  'bytes-auto': { kind: 'family', family: 'bytes', index: -1 },
  'bytes-si': { kind: 'family', family: 'bytes_si', index: -1 },
  bps: { kind: 'family', family: 'bps', index: -1 },
  Bps: { kind: 'family', family: 'Bps', index: -1 },
  Kbps: { kind: 'suffix', text: ' Kbps' },
  Mbps: { kind: 'suffix', text: ' Mbps' },
  Gbps: { kind: 'suffix', text: ' Gbps' },
  KBps: { kind: 'suffix', text: ' KiB/s' },
  MBps: { kind: 'suffix', text: ' MiB/s' },
  GBps: { kind: 'suffix', text: ' GiB/s' },
  // Time family is special: stored in the chosen unit and rendered as
  // "1d 3h 24m" or "1.42 s" depending on magnitude.
  ms: { kind: 'time', baseSeconds: 1 / 1000 },
  s: { kind: 'time', baseSeconds: 1 },
  min: { kind: 'time', baseSeconds: 60 },
  h: { kind: 'time', baseSeconds: 3600 },
  'req/s': { kind: 'suffix', text: ' req/s' },
  'ops/s': { kind: 'suffix', text: ' ops/s' },
};

const formatNumber = (n, decimals, separator) => {
  let v = Number(n).toFixed(decimals);
  if (separator) v = v.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return v;
};

// Auto-scale within a unit family. Returns { value, unit }.
const scaleFamily = (rawValue, family, fixedIndex) => {
  const { base, units } = UNIT_FAMILIES[family];
  const abs = Math.abs(rawValue);
  let i = fixedIndex >= 0 ? fixedIndex : 0;
  let v = rawValue;
  if (fixedIndex >= 0) {
    v = rawValue / Math.pow(base, fixedIndex);
  } else if (abs > 0) {
    i = Math.min(units.length - 1, Math.floor(Math.log(abs) / Math.log(base)));
    if (i > 0) v = rawValue / Math.pow(base, i);
  }
  return { value: v, unit: units[i] };
};

// Pretty-print a duration (input always in seconds — tune in caller).
const formatDuration = (seconds) => {
  const abs = Math.abs(seconds);
  if (abs < 0.001) return (seconds * 1_000_000).toFixed(0) + ' μs';
  if (abs < 1) return (seconds * 1000).toFixed(0) + ' ms';
  if (abs < 60) return seconds.toFixed(2) + ' s';
  if (abs < 3600) {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds - m * 60);
    return s ? `${m}m ${s}s` : `${m}m`;
  }
  if (abs < 86400) {
    const h = Math.floor(seconds / 3600);
    const m = Math.round((seconds - h * 3600) / 60);
    return m ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(seconds / 86400);
  const h = Math.round((seconds - d * 86400) / 3600);
  return h ? `${d}d ${h}h` : `${d}d`;
};

// Format a number value based on widget configuration.
// Backward compatible with the old signature: formatValue(value, { unit, decimalPlaces, useThousandSeparator }).
export const formatValue = (value, config = {}) => {
  if (value === null || value === undefined || (typeof value === 'number' && isNaN(value))) return 'N/A';

  const { useThousandSeparator = true, unit = '' } = config;

  const num = parseFloat(value);
  if (isNaN(num)) return 'N/A';

  // A hard `decimalPlaces = 2` default rendered every unitless count as
  // "1,234.00". Honour an explicit setting, but when the widget hasn't chosen
  // one, don't invent precision the value doesn't have: whole numbers render
  // whole, fractional values keep two places.
  const decimalPlaces = config.decimalPlaces !== undefined && config.decimalPlaces !== null
    ? config.decimalPlaces
    : (Number.isInteger(num) ? 0 : 2);

  const lookup = UNIT_LOOKUP[unit];
  if (!lookup) {
    return formatNumber(num, decimalPlaces, useThousandSeparator) + (unit ? ' ' + unit : '');
  }
  if (lookup.kind === 'suffix') {
    return formatNumber(num, decimalPlaces, useThousandSeparator) + lookup.text;
  }
  if (lookup.kind === 'time') {
    return formatDuration(num * lookup.baseSeconds);
  }
  if (lookup.kind === 'family') {
    const scaled = scaleFamily(num, lookup.family, lookup.index);
    return formatNumber(scaled.value, decimalPlaces, useThousandSeparator) + (scaled.unit ? ' ' + scaled.unit : '');
  }
  // Default fall-through (shouldn't hit if lookup is well-typed)
  return formatNumber(num, decimalPlaces, useThousandSeparator);
};

/**
 * Y-axis tick formatter — same as formatValue but tighter (no thousand
 * separator on tiny axes, fewer decimals on large numbers). Pass to
 * Recharts as YAxis.tickFormatter.
 */
export const makeAxisFormatter = (unit, decimalPlaces = 2) => (value) => {
  if (value === null || value === undefined || isNaN(value)) return '';
  const abs = Math.abs(value);
  // Auto-tighten decimal places for large values.
  const decimals = abs >= 100 ? 0 : abs >= 10 ? 1 : decimalPlaces;
  return formatValue(value, { unit, decimalPlaces: decimals, useThousandSeparator: false });
};

// Get color scheme colors
export const getColorScheme = (scheme = 'default') => {
  const schemes = {
    default: [
      '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1',
      '#13c2c2', '#eb2f96', '#fadb14', '#a0d911', '#fa8c16'
    ],
    blue: [
      '#0050b3', '#1890ff', '#40a9ff', '#69c0ff', '#91d5ff',
      '#bae7ff', '#d6e4ff', '#adc6ff', '#85a5ff', '#597ef7'
    ],
    green: [
      '#135200', '#389e0d', '#52c41a', '#73d13d', '#95de64',
      '#b7eb8f', '#d9f7be', '#f6ffed', '#237804', '#7cb305'
    ],
    red: [
      '#820014', '#cf1322', '#f5222d', '#ff4d4f', '#ff7875',
      '#ffa39e', '#ffccc7', '#fff1f0', '#a8071a', '#d4380d'
    ],
    purple: [
      '#120338', '#391085', '#531dab', '#722ed1', '#9254de',
      '#b37feb', '#d3adf7', '#efdbff', '#2f54eb', '#597ef7'
    ],
    orange: [
      '#ad2102', '#d4380d', '#fa541c', '#ff7a45', '#ff9c6e',
      '#ffbb96', '#ffd8bf', '#fff2e8', '#fa8c16', '#faad14'
    ]
  };

  return schemes[scheme] || schemes.default;
};

// Get line color by index
export const getLineColor = (index, colorScheme = 'default') => {
  const colors = getColorScheme(colorScheme);
  return colors[index % colors.length];
};

// Get legend configuration for Recharts
export const getLegendConfig = (config = {}) => {
  const { showLegend = true, legendPosition = 'bottom' } = config;

  if (!showLegend) {
    return null;
  }

  const positions = {
    top: { verticalAlign: 'top', height: 36 },
    bottom: { verticalAlign: 'bottom', height: 36 },
    left: { layout: 'vertical', verticalAlign: 'middle', align: 'left', width: 100 },
    right: { layout: 'vertical', verticalAlign: 'middle', align: 'right', width: 100 }
  };

  return positions[legendPosition] || positions.bottom;
};

// Get Y-axis domain configuration
export const getYAxisDomain = (config = {}) => {
  const { yAxisMin, yAxisMax } = config;

  // Cleared number fields yield '' (not undefined/null). Passing that through
  // produced a domain like ['', 'auto'], which recharts cannot interpret and
  // renders as a broken axis. GaugeChart already guarded for this; the shared
  // helper did not.
  const hasBound = (v) => v !== undefined && v !== null && v !== '' && !Number.isNaN(Number(v));

  if (hasBound(yAxisMin) && hasBound(yAxisMax)) {
    return [Number(yAxisMin), Number(yAxisMax)];
  } else if (hasBound(yAxisMin)) {
    return [Number(yAxisMin), 'auto'];
  } else if (hasBound(yAxisMax)) {
    return ['auto', Number(yAxisMax)];
  }

  return ['auto', 'auto'];
};

// Format legend name using template and metric labels
export const formatLegendName = (metric, template) => {
  if (!template || template.trim() === '') {
    // Default: show all labels
    const labels = Object.entries(metric || {})
      .filter(([key]) => !key.startsWith('__'))
      .map(([key, value]) => `${key}="${value}"`)
      .join(', ');
    return labels || 'Series';
  }

  // Replace {{labelName}} or {{label.with.dots}} with actual label value
  let formatted = template;
  // Updated regex to support labels with dots: matches word characters and dots
  const regex = /\{\{([\w.]+)\}\}/g;

  formatted = formatted.replace(regex, (match, labelPath) => {
    // First, try direct access (for labels with dots in their names like "service.name")
    // This handles metric labels which are always flat key-value pairs
    if (metric && metric[labelPath] !== undefined && metric[labelPath] !== null) {
      return metric[labelPath];
    }

    // If not found directly, try nested path traversal (for actual nested objects)
    if (labelPath.includes('.')) {
      const parts = labelPath.split('.');
      let value = metric;

      // Traverse the nested path
      for (const part of parts) {
        if (value && typeof value === 'object') {
          value = value[part];
        } else {
          value = undefined;
          break;
        }
      }

      if (value !== undefined && value !== null) {
        return value;
      }
    }

    // Keep placeholder if label doesn't exist
    return match;
  });

  return formatted;
};

/**
 * Normalize a timestamp to epoch milliseconds.
 * ClickHouse returns milliseconds, legacy PromQL returns seconds.
 * Auto-detects based on magnitude: values < 10^10 are treated as seconds.
 */
export const normalizeTimestamp = (ts) => {
  const num = typeof ts === 'number' ? ts : parseFloat(ts);
  if (isNaN(num)) return Date.now();
  // If below 10 billion, it's epoch seconds — convert to millis
  if (num < 10_000_000_000) {
    return num * 1000;
  }
  return num;
};

/**
 * Convert a dashboard time-range string ('30m', '1h', '24h', '7d', '1M', '1y')
 * into a pinned x-axis domain [fromMs, toMs] ending now. Returns null for
 * unparseable input so charts can fall back to a data-derived domain.
 * Pinning the axis to the REQUESTED window (instead of dataMin/dataMax) is
 * what keeps every widget on a dashboard showing the same time range — sparse
 * or missing data renders as visible gaps, never as a shrunken private axis.
 */
export const parseTimeRangeToDomain = (timeRange) => {
  if (!timeRange || typeof timeRange !== 'string') return null;
  const tr = timeRange.trim();
  // Absolute range from the picker: "custom:<startMs>:<endMs>". A fixed window
  // is the *most* important case to pin — both endpoints are known exactly.
  if (tr.startsWith('custom:')) {
    const [, start, end] = tr.split(':');
    const from = parseInt(start, 10);
    const to = parseInt(end, 10);
    if (Number.isFinite(from) && Number.isFinite(to) && to > from) return [from, to];
    return null;
  }
  const m = tr.match(/^(\d+)([smhdMy])$/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  const unitMs = { s: 1000, m: 60_000, h: 3_600_000, d: 86_400_000, M: 2_592_000_000, y: 31_536_000_000 }[m[2]];
  if (!unitMs || !n) return null;
  // Quantize "now" to 30s so the domain is referentially stable across renders
  // (chart React.memo comparisons stay effective between refresh cycles).
  const to = Math.ceil(Date.now() / 30_000) * 30_000;
  return [to - n * unitMs, to];
};

// Process timeseries data for Recharts (shared utility)
export const processTimeseriesData = (timeseriesData, legendTemplate = '') => {
  const seriesMap = {};
  const timestamps = new Set();
  const series = [];

  // Extract all unique timestamps and series
  timeseriesData.forEach((seriesData, index) => {
    const seriesName = `series${index}`;
    series.push(seriesName);

    // Check if series has per-query config (from multi-query support)
    const perQueryLegendTemplate = seriesData._queryConfig?.legendTemplate;
    const effectiveLegendTemplate = perQueryLegendTemplate || legendTemplate;

    // Format series name using template or default labels
    if (seriesData.metric) {
      const formattedName = formatLegendName(seriesData.metric, effectiveLegendTemplate);
      if (formattedName) {
        series[index] = formattedName;
      }
    }

    seriesData.values.forEach(point => {
      const timestamp = normalizeTimestamp(point[0]);
      const raw = typeof point[1] === 'number' ? point[1] : parseFloat(point[1]);
      const value = isNaN(raw) ? null : raw;

      timestamps.add(timestamp);

      if (!seriesMap[timestamp]) {
        seriesMap[timestamp] = {};
      }

      seriesMap[timestamp][series[index]] = value;
    });
  });

  // Create data points for all timestamps (already in milliseconds)
  const dataPoints = Array.from(timestamps).sort((a, b) => a - b).map(timestamp => {
    const point = { timestamp };

    series.forEach(seriesName => {
      point[seriesName] = seriesMap[timestamp]?.[seriesName] ?? null;
    });

    return point;
  });

  return { dataPoints, series };
};
